import React, { useState } from 'react'
import {
  Button,
  Row,
  Col,
  Form,
  FileDropzone,
  CenteredSpinner
} from 'react-angle-dashboard-components'
import DatePicker from 'react-datepicker'
import ReactInputMask from 'react-input-mask'
import { NotFoundCard, SmallTitle, FormLabel } from '../styles'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { claimsService } from '../../../services'

const NoResultTicketSchema = z.object({
  // Information needed
  servicingNPIOrTIN: z.string().nonempty({ message: 'You must provide NPI or TIN' }),
  memberId: z.string().nonempty({ message: 'You must provide NPI or TIN' }),
  firstName: z.string().nonempty({ message: 'Missing First Name' }),
  lastName: z.string().nonempty({ message: 'Missing Last Name' }),
  dateOfBirth: z.date({ required_error: 'Please select a date' }),
  dateOfService: z.date({ required_error: 'Please select a date' }),
  providerOrFacilityName: z.string().nonempty({ message: 'Please include Provider or Facility Name' }),
  providerContactName: z.string().nonempty({ message: 'Please include Provider Contact Name' }),
  providerPhoneNumber: z.string().nonempty({ message: 'Please include Provider Phone Number' }),
  providerEmail: z.string().nonempty({ message: 'Please include Provider Email' }),
  billedAmount: z.number(),
  dateOfCheckIssue: z.date({ required_error: 'Please select a date' }),
  unfoundClaimContext: z.string().optional(),
  unfoundClaimFiles: z.any().optional(),
  // Requestor info
  requestorName: z.string().nonempty({ message: 'Please include requestor name' }),
  email: z.string().nonempty({ message: 'Please include email' }),
  phone: z.string().optional(),
})

export type NoResultTicketFormFields = {
  servicingNPIOrTIN: string
  memberId: string
  firstName: string
  lastName: string
  dateOfBirth: Date
  dateOfService: Date
  providerOrFacilityName: string
  providerContactName: string
  providerPhoneNumber: string
  providerEmail: string
  billedAmount: number
  dateOfCheckIssue: Date
  unfoundClaimContext: string
  unfoundClaimFiles: File[]

  requestorName: string
  email: string
  phone: string
}

export type NoResultTicketReturnData = {
  success: string
}

export const NoResultTicketForm: React.FC<{
  searchQuery: any,
  clearResults: (msg: string) => void
}> = ({
  searchQuery,
  clearResults
}) => {

  const [unfoundClaimFiles, setUnfoundClaimFiles] = useState<File[]>([])
  const [fileDropzoneKey, setFileDropzoneKey] = useState(1)

  const clearAllInputs = () => {
    setUnfoundClaimFiles([])
    setFileDropzoneKey((k) => k + 1)
    clearResults("Thank you, we'll be in touch soon!")
  }

  const createTicket = useMutation((data: FormData) => {
    return claimsService.createNoResultTicket(data)
  })

  const { control, register, handleSubmit, formState, trigger } =
    useForm<NoResultTicketFormFields>({
      defaultValues: {
        servicingNPIOrTIN: searchQuery.provider_id ? searchQuery.provider_id : '',
        memberId: searchQuery.angle_id ? searchQuery.angle_id : '',
        firstName: searchQuery.first_name ? searchQuery.first_name : '',
        lastName: searchQuery.last_name ? searchQuery.last_name : '',
        dateOfBirth: '',
        dateOfService: '',
        providerOrFacilityName: '',
        providerContactName: '',
        providerPhoneNumber: '',
        providerEmail: '',
        billedAmount: searchQuery.billed_amount ? searchQuery.billed_amount : '',
        unfoundClaimContext: '',
        unfoundClaimFiles: [],

        requestorName: '',
        email: '',
        phone: ''
      },
      resolver: zodResolver(NoResultTicketSchema)
    })

  const errors = formState.errors

  const handleCreate: SubmitHandler<NoResultTicketFormFields> = async (
    data
  ) => {
    await trigger()
    const hasErrors = Object.keys(errors).length > 0

    if (!hasErrors) {
      const submitData: any = {}

      submitData.requestor_name = data.requestorName
      const email = data.email
      submitData.contact = { email }
      if (data.phone) {
        submitData.contact.phone = data.phone
      }

      const criteria: any = {}
      criteria.provider_id = data.servicingNPIOrTIN
      criteria.angle_id = data.memberId
      criteria.billed_amount = data.billedAmount
      criteria.date_of_birth = data.dateOfBirth
      criteria.claim_number = searchQuery.claim_number
      submitData.criteria = criteria

      var contextStr: string = ""
      contextStr += "\nfirst_name: " + data.firstName
      contextStr += "\nlast_name: " + data.lastName
      contextStr += "\ndata_of_service: " + data.dateOfService
      contextStr += "\ndate_of_check_issue: " + data.dateOfCheckIssue
      contextStr += "\nbilled_amount: " + data.billedAmount
      contextStr += "\nprovider_name: " + data.providerOrFacilityName
      contextStr += "\nprovider_contact: " + data.providerContactName
      contextStr += "\nprovider_phone: " + data.providerPhoneNumber
      contextStr += "\nprovider_email: " + data.providerEmail
      contextStr += "\nadditional_info: " + data.unfoundClaimContext
      submitData.issue = {
        context: contextStr,
        type: 'email',
        reason: 'Cant find claim'
      }

      const formData = new FormData()
      formData.append('data', JSON.stringify(submitData))
      if (unfoundClaimFiles) {
        unfoundClaimFiles.forEach((f) => {
          formData.append('file', f, f.name)
        })
      }
      try {
        await createTicket.mutateAsync(formData)
        clearAllInputs()
      } catch(e) {
        console.error(e)
      }
    }
  }

  return (
    <>
      <NotFoundCard>
        <h5>
          We do not have a claim on file that matches the information you
          entered
        </h5>
        <p className="text-muted">
          If you believe that all of the information you entered is correct, the
          most common reason for this no matching claim message is that the
          claim submission form was rejected due to non-matching member details
          such as name or date of birth. To search for another claim, update the
          form fields above.
        </p>
      </NotFoundCard>
      <SmallTitle style={{ marginTop: 44 }}>
        Can't find your claim? Submit a ticket below.
      </SmallTitle>
      <h5 style={{ marginBottom: 30}}>
        In order to check claim status, please provide the following information
      </h5>

      <Row>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Member ID</FormLabel>
            <Form.Control
              type="text"
              placeholder="Please enter your Member ID"
              className="bg-white"
              isInvalid={Boolean(errors?.memberId)}
              {...register('memberId', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Servicing NPI or TIN</FormLabel>
            <Form.Control
              type="text"
              placeholder="Servicing NPI or TIN"
              className="bg-white"
              isInvalid={Boolean(errors?.firstName)}
              {...register('servicingNPIOrTIN', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>First Name</FormLabel>
            <Form.Control
              type="text"
              placeholder="First Name"
              className="bg-white"
              isInvalid={Boolean(errors?.firstName)}
              {...register('firstName', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Last Name</FormLabel>
            <Form.Control
              type="text"
              placeholder="Last Name"
              className="bg-white"
              isInvalid={Boolean(errors?.lastName)}
              {...register('lastName', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Date of Birth</FormLabel>
            <Controller
              control={control}
              name="dateOfBirth"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  required
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select a date"
                  onChange={onChange}
                  selected={value}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Date of Service</FormLabel>
            <Controller
              control={control}
              name="dateOfService"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  required
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select a date"
                  onChange={onChange}
                  selected={value}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Provider or Facility Name</FormLabel>
            <Form.Control
              type="text"
              placeholder="Please enter name of Provider or Facility"
              className="bg-white"
              isInvalid={Boolean(errors?.providerOrFacilityName)}
              {...register('providerOrFacilityName', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Provider Contact Name</FormLabel>
            <Form.Control
              type="text"
              placeholder="Please enter Contact name"
              className="bg-white"
              isInvalid={Boolean(errors?.providerContactName)}
              {...register('providerContactName', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Provider Phone Number</FormLabel>
            <Form.Control
              type="tel"
              placeholder="Please enter a phone number"
              className="bg-white"
              as={ReactInputMask}
              mask="999-999-9999"
              isInvalid={Boolean(errors?.providerPhoneNumber)}
              {...register('providerPhoneNumber', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Provider Email Address</FormLabel>
            <Form.Control
              type="email"
              placeholder="Please enter email address"
              className="bg-white"
              isInvalid={Boolean(errors?.providerEmail)}
              {...register('providerEmail', {
                required: 'This field is required'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Billed Amount</FormLabel>
            <Form.Control
              type="number"
              className="bg-white"
              placeholder="Enter a valid amount"
              isInvalid={Boolean(errors?.billedAmount)}
              {...register('billedAmount', {
                required: 'This field is required',
                valueAsNumber: true
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Date of Check Issue</FormLabel>
            <Controller
              control={control}
              name="dateOfCheckIssue"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  required
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select a date"
                  onChange={onChange}
                  selected={value}
                />
              )}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row style={{ marginBottom: 15 }}>
        <Col>
          <FormLabel>
            Please provide any additional context for your inquiry.
          </FormLabel>
          <Form.Control
            as="textarea"
            className="bg-white w-100"
            isInvalid={Boolean(errors?.unfoundClaimContext)}
            {...register('unfoundClaimContext', {})}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 15 }}>
          <Col>
            <FormLabel>Upload Documents/Files</FormLabel>
            <FileDropzone
              key={fileDropzoneKey}
              onFileLoaded={(e: File[]) => {
                setUnfoundClaimFiles(e)
              }}
            />
          </Col>
      </Row>
      <Row>
        <Col>
          <Form.Group>
            <FormLabel>Requestor name</FormLabel>
            <Form.Control
              type="text"
              placeholder='Please enter a requestor name'
              className="bg-white"
              isInvalid={Boolean(errors?.requestorName)}
              {...register('requestorName', {
                required: 'Please enter a requestor name'
              })}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Email</FormLabel>
            <Form.Control
              type="email"
              required
              placeholder="Please enter an email address"
              className="bg-white"
              isInvalid={Boolean(errors?.email)}
              {...register('email', {
                required: 'Please enter an email address'
              })}
            />
          </Form.Group>
        </Col>
        <Col sm={6} xs={12}>
          {' '}
          <Form.Group>
            <FormLabel>Phone (optional)</FormLabel>
            <Form.Control
              type="tel"
              placeholder="Please enter a phone number"
              className="bg-white"
              as={ReactInputMask}
              mask="999-999-9999"
              isInvalid={Boolean(errors?.phone)}
              {...register('phone', {})}
            />
          </Form.Group>
        </Col>
      </Row>{' '}
      {createTicket.isLoading ? (
        <CenteredSpinner />
      ) : (
        <Button className="my-3 py-2" type="submit"
          onClick={
            handleSubmit(handleCreate)
          }>
          Submit
        </Button>
      )}
      {createTicket.isError && (
        <Col
          className="mt-5 py-3 px-4 fs-20"
          sm={6}
          xs={12}
          style={{
            background: 'rgba(237, 76, 120, 0.1)',
            borderRadius: 6
          }}
        >
          {' '}
          An unexpected error occurred creating your ticket. Please reach out
          to our Care Team by phone at +1 (855) 937-1811.
        </Col>
      )}
    </>
  )
}

